import React, { useState, useEffect, useRef } from "react";
import { authenticateUser, attemptIPAutoAuth, isLocalhost } from "./authUtils";
import { debugLog, debugInfo } from "../utils/debug";
import { useLocation } from "react-router-dom";

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const hasAttemptedAutoLogin = useRef(false);
  const location = useLocation();

  useEffect(() => {
    // Проверяем URL параметры для автоматической авторизации
    const params = new URLSearchParams(location.search);
    const urlEmail = params.get('email');
    const urlPassword = params.get('password');
    
    if (urlEmail && urlPassword && !hasAttemptedAutoLogin.current) {
      hasAttemptedAutoLogin.current = true;
      debugInfo("Login", "Auto-login from URL parameters");
      attemptUrlAutoLogin(urlEmail, urlPassword);
      return;
    }

    // Автоматическая авторизация для localhost в режиме разработки
    // Используем ref чтобы предотвратить повторные попытки
    if (!hasAttemptedAutoLogin.current && isLocalhost() && import.meta.env.NODE_ENV === "development") {
      hasAttemptedAutoLogin.current = true;
      attemptAutoLogin();
    }
  }, [location]);

  const attemptUrlAutoLogin = async (email, password) => {
    setIsAutoLogin(true);
    debugInfo("Login", "Attempting auto-login from URL parameters for:", email);

    try {
      const result = await authenticateUser(email, password);
      
      if (result.success) {
        debugInfo("Login", "URL auto-login successful");
        // Очищаем параметры из URL для безопасности
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
        onLogin(result.user);
        return;
      } else {
        debugLog("Login", "URL auto-login failed:", result.error);
        setError(result.error || "Ошибка автоматической авторизации");
      }
    } catch (error) {
      debugLog("Login", "URL auto-login error:", error);
      setError("Ошибка автоматической авторизации");
    }

    setIsAutoLogin(false);
  };

  const attemptAutoLogin = async () => {
    setIsAutoLogin(true);
    debugInfo("Login", "Attempting auto-login for localhost");

    try {
      const user = await attemptIPAutoAuth();
      if (user) {
        onLogin(user);
        return;
      }
    } catch (error) {
      debugLog("Login", "Auto-login failed:", error);
    }

    setIsAutoLogin(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Очищаем ошибку при изменении полей
    if (error) {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const result = await authenticateUser(formData.email, formData.password);

      if (result.success) {
        debugInfo("Login", "Login successful");
        onLogin(result.user);
      } else {
        setError(result.error || "Ошибка авторизации");
      }
    } catch (error) {
      debugLog("Login", "Login error:", error);
      setError("Произошла ошибка при входе в систему");
    } finally {
      setIsLoading(false);
    }
  };

  if (isAutoLogin) {
    return (
      <div className="login-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Автоматическая авторизация...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <h2>Вход в систему</h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            disabled={isLoading}
            placeholder="Введите ваш email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Пароль:</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            disabled={isLoading}
            placeholder="Введите пароль"
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? "Вход..." : "Войти"}
        </button>
      </form>

      <div
        className="auth-links"
        style={{ marginTop: "15px", textAlign: "center" }}
      >
        <a
          href="/forgot-password"
          style={{ color: "#007bff", textDecoration: "none", fontSize: "14px" }}
        >
          Забыли пароль?
        </a>
      </div>
    </div>
  );
};

export default Login;
